import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Cpu, Code2, BarChart3, Compass, Palette, Sparkles,
  ArrowRight, ChevronDown, MessageCircle, Calendar, Check,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, breadcrumbSchema, faqSchema, serviceSchema, organizationSchema } from "../lib/useSeo";
import { whatsappLink } from "../lib/whatsapp";
import { SERVICE_CONTENT, SERVICE_ORDER } from "../lib/serviceContent";
import { ServiceHeroVisual } from "../components/sections/ServiceHeroVisual";

const ICONS = { Cpu, Code2, BarChart3, Compass, Palette, Sparkles };

const Section = ({ id, eyebrow, title, children, className = "" }) => (
  <section id={id} className={`px-6 md:px-12 lg:px-24 py-20 md:py-24 ${className}`}>
    <div className="max-w-6xl mx-auto">
      {(eyebrow || title) && (
        <div className="mb-12">
          {eyebrow && (
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-brand-orange" />
              <span className="label-mono">{eyebrow}</span>
            </div>
          )}
          {title && (
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-[44px] tracking-tight text-white leading-[1.1] max-w-3xl">
              {title}
            </h2>
          )}
        </div>
      )}
      {children}
    </div>
  </section>
);

const Breadcrumb = ({ items }) => (
  <nav aria-label="Breadcrumb" className="px-6 md:px-12 lg:px-24 pt-28 md:pt-32">
    <ol className="max-w-6xl mx-auto flex items-center gap-2 text-xs font-mono text-white/50">
      {items.map((it, i) => (
        <li key={it.name} className="flex items-center gap-2">
          {i > 0 && <span className="text-white/30">/</span>}
          {it.url ? (
            <Link to={it.url} className="hover:text-brand-orange transition-colors">{it.name}</Link>
          ) : (
            <span className="text-white/80">{it.name}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div
      data-testid={`svc-faq-${i}`}
      className="glass rounded-2xl px-6 md:px-7 py-5 transition-colors hover:border-brand-orange/30"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-6 text-left"
        aria-expanded={open}
      >
        <h3 className="font-display font-semibold text-white text-base md:text-lg pr-4">{q}</h3>
        <ChevronDown className={`w-5 h-5 text-white/60 shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-brand-orange" : ""}`} />
      </button>
      <div
        className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <p className="text-white/70 leading-relaxed text-[15px]">{a}</p>
        </div>
      </div>
    </div>
  );
};

const CTABand = ({ locale }) => {
  const isDE = locale === "de";
  const presetMsg = isDE
    ? "Hallo Joshi FusionTech Media, ich habe eine Frage zu eurer Leistung."
    : "Hi Joshi FusionTech Media, I have a question about your services.";
  return (
    <section className="px-6 md:px-12 lg:px-24 py-20">
      <div className="relative max-w-6xl mx-auto glass-strong rounded-3xl overflow-hidden p-10 md:p-14 lg:p-16">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-brand-orange" />
              <span className="label-mono">{isDE ? "Bereit zu starten?" : "Ready to start?"}</span>
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]">
              {isDE
                ? "Lass uns über dein Projekt sprechen."
                : "Let's talk about your project."}
            </h2>
            <p className="mt-5 text-white/65 max-w-xl text-base md:text-lg leading-relaxed">
              {isDE
                ? "30-minütiges Strategiegespräch, kostenlos und unverbindlich. Wir hören zu, denken laut mit, geben konkrete nächste Schritte."
                : "30-minute strategy call, free and non-binding. We listen, think with you and give concrete next steps."}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/book"
              data-testid="svc-cta-calendly"
              className="btn-primary !justify-center"
            >
              <Calendar className="w-4 h-4" />
              {isDE ? "Kostenloses Beratungsgespräch" : "Book a free call"}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={whatsappLink(presetMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="svc-cta-whatsapp"
              className="btn-secondary !justify-center !border-[#25D366]/40 hover:!border-[#25D366]"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              {isDE ? "Schnelle Antwort via WhatsApp" : "Quick reply via WhatsApp"}
            </a>
            <Link
              to="/contact"
              data-testid="svc-cta-contact"
              className="btn-ghost !justify-center"
            >
              {isDE ? "Oder Kontaktformular nutzen" : "Or use the contact form"}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const InternalLinks = ({ slug, locale }) => {
  const isDE = locale === "de";
  const others = SERVICE_ORDER.filter((s) => s !== slug);
  return (
    <section className="px-6 md:px-12 lg:px-24 pb-20">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mb-7">
          {isDE ? "Weitere Leistungen entdecken" : "Explore more services"}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {others.map((s) => {
            const ic = SERVICE_CONTENT[s];
            const Icon = ICONS[ic.icon];
            const label = ic.hero[locale].kicker;
            return (
              <Link
                key={s}
                to={`/services/${s}`}
                data-testid={`svc-internal-${s}`}
                className="glass rounded-2xl p-4 hover:border-brand-orange/40 transition-all group"
              >
                <Icon className="w-5 h-5 text-brand-orange mb-3" />
                <div className="text-sm text-white/80 group-hover:text-white transition-colors">{label}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const isDE = locale === "de";

  const exists = !!SERVICE_CONTENT[slug];
  const c = exists ? SERVICE_CONTENT[slug] : SERVICE_CONTENT["ai-automation"];
  const Icon = ICONS[c.icon] || Cpu;
  const L = (obj) => obj?.[locale] || obj?.en || obj;
  const pageUrl = `https://www.joshifusiontech.de/services/${slug}`;

  const breadcrumbs = [
    { name: isDE ? "Start" : "Home", url: "/" },
    { name: isDE ? "Leistungen" : "Services", url: "/services" },
    { name: L(c.hero).kicker },
  ];

  useSeo({
    title: c.seoTitle[locale],
    description: c.seoDesc[locale],
    canonical: pageUrl,
    locale,
    jsonLd: exists
      ? [
          organizationSchema(),
          serviceSchema({
            name: L(c.hero).kicker,
            description: c.seoDesc[locale],
            url: pageUrl,
            slug,
          }),
          breadcrumbSchema(
            breadcrumbs.map((b) => ({
              name: b.name,
              url: b.url ? `https://www.joshifusiontech.de${b.url}` : pageUrl,
            }))
          ),
          faqSchema(c.faq[locale]),
        ]
      : null,
  });

  if (!exists) {
    return <Navigate to="/services" replace />;
  }

  const hero = L(c.hero);
  const deliver = c.deliver ? L(c.deliver) : null;
  const services = c.services ? L(c.services) : null;
  const benefits = c.benefits ? L(c.benefits) : null;
  const stack = c.stack ? L(c.stack) : null;
  const framework = c.framework ? L(c.framework) : null;
  const deliverables = c.deliverables ? L(c.deliverables) : null;
  const process = L(c.process);
  const cases = c.cases ? L(c.cases) : null;
  const education = c.education ? L(c.education) : null;
  const faq = c.faq[locale];

  return (
    <main data-testid={`service-page-${slug}`}>
      <Breadcrumb items={breadcrumbs} />

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 pt-10 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-px bg-brand-orange" />
                <span className="label-mono">{hero.kicker}</span>
              </div>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-[64px] tracking-tight text-white leading-[1.04]">
                {hero.h1}
              </h1>
              <p className="mt-7 text-lg md:text-xl text-white/65 leading-relaxed max-w-2xl">{hero.sub}</p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link to="/book" data-testid="hero-cta-calendly" className="btn-primary">
                  <Calendar className="w-4 h-4" />
                  {isDE ? "Kostenloses Beratungsgespräch" : "Book a free call"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" data-testid="hero-cta-contact" className="btn-secondary">
                  {isDE ? "Kontakt aufnehmen" : "Get in touch"}
                </Link>
              </div>
            </div>
            <div className="glass rounded-3xl p-7 md:p-9 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
              <div className="w-14 h-14 rounded-2xl bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center mb-5">
                <Icon className="w-6 h-6 text-brand-orange" />
              </div>
              <div className="label-mono text-brand-orange mb-2">{isDE ? "Spezialisierung" : "Specialization"}</div>
              <div className="font-display text-xl md:text-2xl text-white tracking-tight mb-5">{hero.kicker}</div>
              <ul className="space-y-2 text-sm text-white/70">
                {(deliver || services)?.items?.slice(0, 4).map((d, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                    <span>{d.t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Deliver / Services */}
      <ServiceHeroVisual slug={slug} locale={locale} />
      {(deliver || services) && (
        <Section eyebrow={isDE ? "Leistungen" : "Deliverables"} title={(deliver || services).title}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(deliver || services).items.map((it, i) => (
              <article key={it.t} data-testid={`svc-item-${i}`} className="glass rounded-2xl p-7 hover:border-brand-orange/30 transition-all">
                <div className="font-display font-semibold text-lg text-white mb-2">{it.t}</div>
                <p className="text-sm text-white/65 leading-relaxed">{it.d}</p>
              </article>
            ))}
          </div>
        </Section>
      )}

      {/* Benefits or Education (digital marketing) */}
      {education && (
        <Section eyebrow={isDE ? "Warum es wichtig ist" : "Why it matters"} title={education.title}>
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-3xl mb-10">{education.intro}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
            {education.why.map((w) => (
              <div key={w.t} className="glass rounded-2xl p-5">
                <div className="font-display font-semibold text-white mb-2">{w.t}</div>
                <p className="text-sm text-white/60">{w.d}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="glass rounded-3xl p-7 md:p-9">
              <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-5">{education.analyticsTitle}</h3>
              <ul className="space-y-3 text-white/75">
                {education.analytics.map((a) => (
                  <li key={a} className="flex items-start gap-3 text-[15px]">
                    <BarChart3 className="w-4 h-4 text-brand-orange mt-1 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass rounded-3xl p-7 md:p-9">
              <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-5">{education.seoTitle}</h3>
              <ul className="space-y-3 text-white/75">
                {education.seo.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-[15px]">
                    <Check className="w-4 h-4 text-brand-orange mt-1 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      )}

      {benefits && (
        <Section eyebrow={isDE ? "Nutzen" : "Benefits"} title={benefits.title}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {benefits.items.map((b, i) => (
              <div key={b} className="glass rounded-2xl p-5 flex flex-col gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center">
                  <span className="label-mono text-brand-orange">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="text-sm text-white/80 leading-relaxed">{b}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Framework (consulting) or Stack */}
      {framework && (
        <Section eyebrow={isDE ? "Beratungs-Framework" : "Framework"} title={framework.title}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {framework.items.map((f, i) => (
              <article key={f.t} className="glass rounded-2xl p-7 hover:border-brand-orange/30 transition-all">
                <div className="label-mono text-brand-orange mb-3">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display font-semibold text-lg text-white mb-2">{f.t}</div>
                <p className="text-sm text-white/65 leading-relaxed">{f.d}</p>
              </article>
            ))}
          </div>
        </Section>
      )}

      {stack && (
        <Section eyebrow={isDE ? "Tech-Stack" : "Tech stack"} title={stack.title}>
          <div className="flex flex-wrap gap-2.5">
            {stack.items.map((s) => (
              <span key={s} className="px-4 py-2 rounded-full glass text-sm text-white/80 hover:border-brand-orange/40 transition-colors">
                {s}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Process */}
      <Section eyebrow={isDE ? "Ablauf" : "Process"} title={process.title}>
        <div className="relative pl-6 md:pl-10">
          <div className="absolute left-2 md:left-4 top-2 bottom-2 w-px bg-gradient-to-b from-brand-orange via-brand-orange/30 to-transparent" />
          {process.steps.map((s, i) => (
            <div key={s.t} className="relative mb-10 last:mb-0">
              <div className="absolute -left-[26px] md:-left-[34px] top-1 w-7 h-7 rounded-full bg-brand-orange flex items-center justify-center text-[11px] font-mono text-white shadow-[0_0_20px_rgba(255,106,0,0.5)]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-display font-bold text-xl md:text-2xl text-white tracking-tight">{s.t}</h3>
              <p className="mt-2 text-white/65 leading-relaxed max-w-2xl">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Deliverables list */}
      {deliverables && (
        <Section eyebrow={isDE ? "Deliverables" : "Deliverables"} title={deliverables.title}>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {deliverables.items.map((d) => (
              <li key={d} className="glass rounded-xl px-5 py-4 text-sm text-white/80 flex items-start gap-3">
                <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Case studies */}
      {cases && (
        <Section eyebrow={isDE ? "Beispiele" : "Examples"} title={cases.title}>
          <div className="grid md:grid-cols-2 gap-6">
            {cases.items.map((cs, i) => (
              <article key={cs.challenge} data-testid={`svc-case-${i}`} className="glass rounded-3xl p-7 md:p-9 hover:border-brand-orange/30 transition-all">
                <div className="label-mono text-brand-orange mb-4">{cs.industry}</div>
                <dl className="space-y-4 text-sm">
                  <div>
                    <dt className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/45 mb-1">{isDE ? "Herausforderung" : "Challenge"}</dt>
                    <dd className="text-white/85 leading-relaxed">{cs.challenge}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/45 mb-1">{isDE ? "Lösung" : "Solution"}</dt>
                    <dd className="text-white/85 leading-relaxed">{cs.solution}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/45 mb-1">{isDE ? "Ergebnis" : "Result"}</dt>
                    <dd className="text-white/85 leading-relaxed">{cs.result}</dd>
                  </div>
                </dl>
                <div className="mt-5 text-[11px] font-mono uppercase tracking-[0.2em] text-white/35">
                  {isDE ? "Showcase-Beispiel · kein realer Kundenname" : "Showcase example · not a real client name"}
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}

      {/* FAQ */}
      <Section eyebrow="FAQ" title={isDE ? "Häufig gestellte Fragen" : "Frequently asked questions"}>
        <div className="space-y-3 max-w-4xl">
          {faq.map((it, i) => (
            <FaqItem key={it.q} q={it.q} a={it.a} i={i} />
          ))}
        </div>
      </Section>

      <CTABand locale={locale} />
      <InternalLinks slug={slug} locale={locale} />
    </main>
  );
}
